import { useEffect, useState } from 'react';
import {
  InvoiceIcmsCstCsosn,
  InvoiceItemFormValues,
  TaxRegimeEnum,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';
import { Badge, Button, Divider, Form, FormInstance } from 'antd';
import NoteItemsCofinsTabsForm from './NoteItemsCofinsTabsForm';
import NoteItemsGeneralTabsForm from './NoteItemsGeneralNfeTabsForm';
import NoteItemsIcmsTabsForm from './NoteItemsIcmsTabsForm';
import NoteItemsIpiTabsForm from './NoteItemsIpiTabsForm';
import NoteItemsPisTabsForm from './NoteItemsPisTabsForm';
import NoteItemsShareForm from './NoteItemsShareForm';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ButtonBack from '@/components/_common/ButtonBack';
import TabsCustom from '@/components/_common/TabsCustom';
import { useCalcTaxesItems } from '@/core/domain/invoice/invoiceItem/invoiceItem.hook';
import { useInvoiceSettingsByCompany } from '@/core/domain/invoice/settings/Settings.hooks';
import { useNcmMutation } from '@/core/domain/operational/Ncm/Ncm.hooks';
import {
  optionsCsosnCodes,
  optionsCstCodes,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.const';
import { formSetFieldsValueTributes } from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.functions';
import { InvoiceType } from '@/core/domain/invoice/invoices/invoices.enums';
import NoteItemsFuelTabsForm from './NoteItemsFuelTabsForm';
import { InvoiceCreateDTO } from '@/core/domain/invoice/invoice/Invoice.types';

interface Props {
  form: FormInstance<InvoiceItemFormValues>;
  invoice?: InvoiceCreateDTO;
  invoiceId: number;
  invoiceType: string;
  itemId?: number;
  handleValidateFieldsPart?: () => Promise<boolean | undefined>;
  handleSubmit?: (redirect: boolean) => void;
  tabErrors?: Record<string, boolean>;
  loading?: boolean;
}

export default function InvoiceNoteItemsSelectPartTabs({
  form,
  invoice,
  invoiceId,
  invoiceType,
  itemId,
  handleValidateFieldsPart,
  handleSubmit,
  tabErrors,
  loading,
}: Props) {
  const [menuKeyActive, setMenuKeyActive] = useState<string>('general');

  const calculateTaxesItems = useCalcTaxesItems();

  const { data: invoiceSetting } = useInvoiceSettingsByCompany(
    invoice?.companyId,
  );

  const ncmIdField = Form.useWatch('ncmId', form);
  const taxRegimeField = Form.useWatch(['itemTaxes', 'taxRegime'], form);
  const valueUnitary = Form.useWatch('valueUnitary', form);
  const quantity = Form.useWatch('quantity', form);

  const ncmMutation = useNcmMutation();

  useEffect(() => {
    if (ncmIdField) {
      changeInvoiceItemData(ncmIdField, taxRegimeField, true);
    }
  }, [ncmIdField, valueUnitary, quantity]);

  const changeInvoiceItemData = async (
    ncmId: number,
    taxRegime: TaxRegimeEnum,
    changeTaxRegime?: boolean,
  ) => {
    const ncmData = await ncmMutation.mutateAsync(ncmId);

    if (ncmData && invoiceSetting) {
      const handleIcmsCst = (cst: string, cson: string) => {
        if (taxRegime !== TaxRegimeEnum.simple)
          return optionsCstCodes.find((a) => a.code === cst)?.id;
        if (taxRegime === TaxRegimeEnum.simple)
          return optionsCsosnCodes.find((a) => a.code === cson)?.id;
      };

      form.setFieldsValue(
        formSetFieldsValueTributes(
          ncmData,
          invoiceSetting,
          invoiceType as InvoiceType,
          handleIcmsCst(
            ncmData.cstIcms,
            ncmData.csosnIcms,
          ) as InvoiceIcmsCstCsosn,
          changeTaxRegime,
          valueUnitary,
          quantity,
        ),
      );
    }
  };

  return (
    <>
      <TabsCustom
        key={menuKeyActive}
        activeKey={menuKeyActive}
        onChange={setMenuKeyActive}
      >
        <TabsCustom.Item
          icon={tabErrors?.general ? <Badge dot /> : null}
          label="Geral"
          key="general"
        >
          <NoteItemsGeneralTabsForm
            form={form}
            invoiceType={invoiceType}
            changeInvoiceItemData={changeInvoiceItemData}
          />
        </TabsCustom.Item>

        {invoiceType !== 'NFS' && (
          <TabsCustom.Item
            icon={tabErrors?.icms ? <Badge dot /> : null}
            label="ICMS"
            key="icms"
          >
            <NoteItemsIcmsTabsForm
              form={form}
              changeInvoiceItemData={changeInvoiceItemData}
            />
          </TabsCustom.Item>
        )}

        {invoiceType !== 'NFS' && (
          <TabsCustom.Item label="Partilha" key="share">
            <NoteItemsShareForm form={form} />
          </TabsCustom.Item>
        )}

        {invoiceType !== 'NFS' && (
          <TabsCustom.Item label="IPI" key="ipi">
            <NoteItemsIpiTabsForm form={form} />
          </TabsCustom.Item>
        )}

        {invoiceType !== 'NFS' && (
          <TabsCustom.Item label="PIS" key="pis">
            <NoteItemsPisTabsForm form={form} />
          </TabsCustom.Item>
        )}

        {invoiceType !== 'NFS' && (
          <TabsCustom.Item label="COFINS" key="cofins">
            <NoteItemsCofinsTabsForm form={form} />
          </TabsCustom.Item>
        )}

        {invoiceType !== 'NFS' && (
          <TabsCustom.Item label="Combustível" key="fuel">
            <NoteItemsFuelTabsForm form={form} />
          </TabsCustom.Item>
        )}
      </TabsCustom>

      <Divider />

      <SpaceForButton>
        <ButtonBack
          path={`/app/fiscal/invoice/${invoiceType}/${invoiceId}/noteItens`}
        />

        {!!itemId && (
          <Button
            type="primary"
            block
            onClick={async () => {
              if (handleValidateFieldsPart) {
                const allFieldsFilled = await handleValidateFieldsPart();

                if (allFieldsFilled && handleSubmit) {
                  await handleSubmit(false);
                  await calculateTaxesItems.mutateAsync(itemId);
                }
              }
            }}
            loading={loading || calculateTaxesItems.isLoading}
          >
            Calcular Tributos
          </Button>
        )}
      </SpaceForButton>
    </>
  );
}
